/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';
import utils from '../../../../common/utils';
import {
  buildEvalByRubricIdPath,
  clearGraphModelError,
  getErrorMessage,
  handleGraphServiceHttpError,
  setNotification
} from './graphServiceUtils';

/** PUT /graphs/:graphId/eval/:rubricId/scheduler */
export default class SaveEvaluationSchedulerService extends BaseService {
  constructor() {
    super(constants.SAVE_EVALUATION_SCHEDULER, 'saveEvaluationSchedulerStatus');
  }

  buildSavedSchedulerFromResponse(data) {
    return {
      evalSamplePercent: data.evalSamplePercent,
      scheduler: data.scheduler ?? null,
      recurring: data.recurring ?? false,
      isConversational: data.isConversational ?? false,
      evalSchedule: data.evalSchedule ?? null,
      rubricName: data.rubricName ?? '',
      ...(data.rubricId != null && data.rubricId !== '' ? { rubricId: String(data.rubricId) } : {})
    };
  }

  publishSaveError(data, msg) {
    setNotification({
      type: 'error',
      langMessage: ['graphEvaluation.SAVE_SCHEDULER_ERROR_PREFIX', ': ', msg]
    });
    Framework.getStoreRegistry().getStore('graphModel').setError(msg);
    Framework.getEventManager().publish(constants.SAVE_EVALUATION_SCHEDULER_ERROR, { data, message: msg });
  }

  async handleEvent(eventName, event) {
    const { payload, headers } = utils.buildRequestPartsFromPayload(event);
    const { graphId, rubricId, requestBody } = payload;
    const url = buildEvalByRubricIdPath(graphId, rubricId, 'scheduler');

    const { isStackB } = utils.getStackBParams();

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackb').put(url, requestBody, { headers: enhancedHeaders });
    }
    return this.getClient('aiadmin').put(url, requestBody, { headers });
  }

  handleResponse(response) {
    const { data } = response;
    if (data && data.success) {
      clearGraphModelError();
      setNotification(
        data?.message
          ? { type: 'success', message: data.message }
          : { type: 'success', langMessage: 'graphEvaluation.SAVE_SCHEDULER_SUCCESS' }
      );
      const savedScheduler = this.buildSavedSchedulerFromResponse(data);
      Framework.getEventManager().publish(constants.SAVE_EVALUATION_SCHEDULER_SUCCESS, {
        data,
        ...savedScheduler
      });
      return;
    }

    const msg = getErrorMessage(data, 'Unknown error');
    this.publishSaveError(data, msg);
  }

  handleError(error) {
    handleGraphServiceHttpError(error, {
      fallbackLangMessage: 'graphEvaluation.SAVE_SCHEDULER_RETRY',
      errorEvent: constants.SAVE_EVALUATION_SCHEDULER_ERROR,
      updateGraphModelError: true
    });
  }
}
